import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchAllInvoices,
  updateInvoiceStatus,
} from "../../store/superAdmin/invoicesSlice";
import { InvoiceType } from "../../types/InvoicesTypes";
import { downloadBoxFile } from "../../components/utilities/downloadFiles";
import { fetchBoxAccessToken } from "../../store/common/authSlice";
import { Helmet } from "react-helmet-async";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminMetaRow,
  AdminTableCard,
  AdminTableState,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";

const statusOptions = ["Pending", "Paid", "Cancelled"];

const AdminInvoices = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { allInvoices, loading, error } = useAppSelector(
    (state) => state.invoices
  );
  const { boxAccessToken } = useAppSelector((state) => state.auth);

  const isReadOnly = user?.role === "Admin Read";

  // Local optimistic UI states
  const [copiedInvoice, setCopiedInvoice] = useState<string | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null
  );
  const [optimisticStatus, setOptimisticStatus] = useState<
    Record<string, string>
  >({});
  const [updatingInvoice, setUpdatingInvoice] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchBoxAccessToken());
    dispatch(fetchAllInvoices());
  }, [dispatch]);

  // Copy payment link
  const handleCopyLink = async (link: string, invoiceNumber: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedInvoice(invoiceNumber);
      setTimeout(() => setCopiedInvoice(null), 2000);
    } catch (err) {
      console.error("Failed to copy link: ", err);
    }
  };

  // ⭐ Optimistic status update
  const handleStatusChange = async (
    invoice: InvoiceType,
    newStatus: string
  ) => {
    const invoiceNumber = invoice.invoice_Number;

    // Store previous value in case of rollback
    const previousStatus = invoice.status;

    // Apply optimistic UI
    setOptimisticStatus((prev) => ({
      ...prev,
      [invoiceNumber]: newStatus,
    }));
    setUpdatingInvoice(invoiceNumber);

    const result = await dispatch(
      updateInvoiceStatus({ invoice_Number: invoiceNumber, status: newStatus })
    );

    setUpdatingInvoice(null);

    // If failed → revert UI
    if (updateInvoiceStatus.rejected.match(result)) {
      setOptimisticStatus((prev) => ({
        ...prev,
        [invoiceNumber]: previousStatus,
      }));
    }
  };

  // Download file from Box
  const handleDownloadFile = async (fileId: string, fileName: string) => {
    if (!boxAccessToken) return;
    setDownloadingFileId(fileId);
    await downloadBoxFile(fileId, fileName, boxAccessToken);
    setDownloadingFileId(null);
  };

  const colCount = 7;

  return (
    <>
      <Helmet>
        <title>All Invoices</title>
      </Helmet>

      <AdminPageShell>
        <AdminPageHeader
          title="Admin Invoices"
          subtitle="Track lab invoices, statuses, and payment links"
        />

        <AdminMetaRow
          left={
            <>
              {loading ? (
                <span>Loading invoices…</span>
              ) : (
                <span>
                  {allInvoices.length} invoice
                  {allInvoices.length === 1 ? "" : "s"}
                </span>
              )}
              {isReadOnly ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-2.5 py-0.5">
                  Read-Only Access
                </span>
              ) : null}
            </>
          }
        />

        <AdminTableCard>
          <table className="table-auto w-full">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                <th className={adminTableHead}>Invoice Number</th>
                <th className={adminTableHead}>Lab Token</th>
                <th className={adminTableHead}>Billing Period</th>
                <th className={adminTableHead}>Amount</th>
                <th className={adminTableHead}>Work Summary</th>
                <th className={adminTableHead}>Status</th>
                <th className={adminTableHead}>Payment Link</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="error"
                  message={error}
                />
              ) : loading ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="loading"
                  message="Loading invoices…"
                />
              ) : allInvoices.length === 0 ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="empty"
                  message="No invoices found"
                />
              ) : (
                allInvoices.map((invoice: InvoiceType) => {
                  const fileIds = invoice.work_Summary?.split(",") || [];
                  const invoiceNumber = invoice.invoice_Number;

                  // Choose optimistic status if available
                  const currentStatus =
                    optimisticStatus[invoiceNumber] || invoice.status;

                  return (
                    <tr
                      key={invoiceNumber}
                      className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF8] transition-colors"
                    >
                      <td className={adminTableCell}>{invoiceNumber}</td>
                      <td className={adminTableCell}>{invoice.lab}</td>
                      <td className={adminTableCell}>
                        {invoice.billing_period}
                      </td>
                      <td className={adminTableCell}>
                        {invoice.amount_due}
                      </td>

                      <td className={adminTableCell}>
                        <div className="flex flex-col gap-1">
                          {fileIds.map((fileId, index) => (
                            <button
                              key={fileId}
                              type="button"
                              onClick={() =>
                                handleDownloadFile(
                                  fileId,
                                  fileIds.length === 1
                                    ? "Summary"
                                    : `Summary ${index + 1}`
                                )
                              }
                              className="text-[#2fa9bf] hover:text-[#2296aa] hover:underline text-left disabled:opacity-50"
                              disabled={downloadingFileId === fileId}
                            >
                              {downloadingFileId === fileId
                                ? "Downloading…"
                                : fileIds.length === 1
                                  ? "Summary"
                                  : `Summary ${index + 1}`}
                            </button>
                          ))}
                        </div>
                      </td>

                      <td className={adminTableCell}>
                        <select
                          value={currentStatus}
                          onChange={(e) =>
                            handleStatusChange(invoice, e.target.value)
                          }
                          className="rounded-lg border border-[#DDDDD8] bg-[#F8F8F6] px-2.5 py-1.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={
                            updatingInvoice === invoiceNumber || isReadOnly
                          }
                        >
                          {statusOptions.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>

                        {updatingInvoice === invoiceNumber && (
                          <p className="text-xs text-[#2fa9bf] mt-1">
                            Updating…
                          </p>
                        )}
                      </td>

                      <td className={adminTableCell}>
                        <button
                          type="button"
                          onClick={() =>
                            handleCopyLink(
                              invoice.invoice_link,
                              invoice.invoice_Number
                            )
                          }
                          className={`inline-flex items-center py-1.5 px-3.5 rounded-full text-xs font-medium transition ${
                            copiedInvoice === invoice.invoice_Number
                              ? "bg-emerald-500 text-white"
                              : "bg-[#2fa9bf] text-white hover:bg-[#2296aa]"
                          }`}
                        >
                          {copiedInvoice === invoice.invoice_Number
                            ? "Copied!"
                            : "Copy Link"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </AdminTableCard>
      </AdminPageShell>
    </>
  );
};

export default AdminInvoices;
